// ── Per-outcome ("per-bin") probability histories for the timeline plot ──
//
// The probability plot draws one line across every reforecast vintage. With a
// distribution marker in place of the probability pie there is no single line to
// draw any more: a five-outcome question has five histories, one per bucket, and
// which one the user wants depends on which bucket they are looking at.
//
// So the series is built for EVERY bucket as the vintages accrue, and the caller
// renders one at a time — the current leader by default, or whichever bucket the
// user is hovering. Building them all is what makes the hover instant and what
// makes a leader change cost nothing: no refetch, the vintages are already loaded.

import { buildValueMoments } from "./reportPointer.js";
import { extractMultiway, binaryBuckets, leadingRow } from "./multiway.js";

/** A bucket list is usable when it is a non-empty array of labelled masses. */
function isBuckets(b) {
  return Array.isArray(b) && b.length > 0;
}

/**
 * The bucket distribution a single vintage carried, or null when it carried none.
 *
 * Mirrors `extractDistribution`'s layering — genuine multi-value first, then the
 * synthesized binary pair under All Multiway — so a bin series and the marker agree
 * on what the buckets ARE for the same forecast. `dist` (a multi-value distribution
 * already extracted upstream) wins when present; otherwise a finite scalar
 * probability becomes the two-slot YES/NO pair, but only when the flag is on. With
 * the flag off a binary vintage yields null, which is what keeps a yes/no question
 * on its P(YES) line in the environments that still draw a pie.
 */
function bucketsAt(dist, probability, allMultiway) {
  if (dist && isBuckets(dist.buckets)) return dist.buckets;
  if (!allMultiway) return null;
  const b = binaryBuckets(probability);
  return isBuckets(b) ? b : null;
}

/**
 * Every vintage's full bucket distribution, on the same timeline as the scalar
 * probability line.
 *
 * Shares `buildValueMoments` with `buildReforecastMoments` rather than re-deriving
 * the manual/async timing convention, so the per-bin line and the scalar line date
 * the same vintage to the same moment.
 *
 * @param {{
 *   history?: Array,             // manual ↻ snapshots (pre-reforecast contracts)
 *   asyncMoments?: Array,        // scheduled fires — `{fireTs, p, dist}`
 *   currentBuckets: Array|null,  // the LIVE distribution (the marker's own buckets)
 *   currentProbability?: number, // live scalar, for the binary fallback
 *   createdAt?: string|number|Date,
 *   allMultiway?: boolean,
 * }} args
 * @returns {Array<{atMs:number, buckets:Array<{label:string, p:number}>}>}
 */
export function buildDistributionMoments({
  history,
  asyncMoments,
  currentBuckets,
  currentProbability,
  createdAt,
  allMultiway = false,
}) {
  const live = isBuckets(currentBuckets)
    ? currentBuckets
    : bucketsAt(null, currentProbability, allMultiway);
  return buildValueMoments({
    history,
    asyncItems: asyncMoments,
    currentValue: live,
    createdAt,
    isValid: isBuckets,
    // A manual snapshot stores the whole pre-reforecast contract, so its distribution
    // is read straight off it — the same shape `extractMultiway` takes elsewhere.
    manualValueOf: (snap) =>
      bucketsAt(extractMultiway(snap?.contract), snap?.contract?.probability, allMultiway),
    asyncValueOf: (m) => bucketsAt(m?.dist, m?.p, allMultiway),
  }).map(({ atMs, value }) => ({ atMs, buckets: value }));
}

/**
 * Match a bucket by LABEL, never by position.
 *
 * Bucket order is display order, and a nominal categorical is ranked by probability —
 * so the bucket at index 2 in one vintage is routinely a different outcome at index 2
 * in the next. Following an index would draw a line that silently hops between
 * outcomes every time the ranking changed. Case- and whitespace-insensitive, since the
 * label is model-authored text that can come back re-cased across runs. A label that
 * differs only in Latin accents ("Inácio" in one vintage, "Inacio" in the next) matches
 * too, but only when exactly one bucket folds to it — the same exact-first,
 * unambiguous-fold rule as `resolvedBucketIndex` — so the line never silently follows a
 * different outcome.
 */
function findByLabel(buckets, label) {
  if (!isBuckets(buckets) || typeof label !== "string") return null;
  const labelled = buckets.filter((b) => typeof b?.label === "string");
  const want = label.trim().toLowerCase();
  const exact = labelled.find((b) => b.label.trim().toLowerCase() === want);
  if (exact) return exact;
  const key = accentKey(want);
  const folded = labelled.filter((b) => accentKey(b.label.trim().toLowerCase()) === key);
  return folded.length === 1 ? folded[0] : null;
}

/**
 * A label with its Latin combining accents removed. Canonical (NFD) decomposition, and only
 * Combining Diacritical Marks that sit on a Latin letter: the same marks distinguish letters
 * in other scripts (Cyrillic \u0439 is not \u0438), and compatibility folding would merge distinct
 * characters.
 */
function accentKey(label) {
  return label
    .normalize("NFD")
    .replace(/(\p{Script=Latin})[\u0300-\u036f]+/gu, "$1")
    .normalize("NFC");
}

/**
 * One bucket's probability history, as the `{atMs, p}` moments the plot already draws.
 *
 * A vintage whose outcome space did not include this label is OMITTED rather than
 * zero-filled: the question was not carrying that outcome then, and plotting a 0%
 * would assert the model had ruled it out. The plot steps between the moments it is
 * given, so an omission reads as the line simply not extending back that far.
 *
 * @param {Array<{atMs:number, buckets:Array}>} distMoments
 * @param {string} label
 * @returns {Array<{atMs:number, p:number}>}
 */
export function seriesForLabel(distMoments, label) {
  const out = [];
  for (const m of Array.isArray(distMoments) ? distMoments : []) {
    const b = findByLabel(m?.buckets, label);
    const p = b?.p;
    if (typeof p === "number" && Number.isFinite(p)) out.push({ atMs: m.atMs, p });
  }
  return out;
}

/**
 * The label of the bucket the plot follows by default: the CURRENT leader.
 *
 * Deliberately the leader as of now, resolved once and then traced backward through
 * every vintage — not whoever led at each moment. A line that re-picked the front-runner
 * at every step would always ride the top of the pack while silently changing which
 * outcome it describes, and it would match no bucket's own hover line. Following one
 * bucket keeps the default line the same KIND of thing as every hover line: one
 * outcome's honest history, including the stretches where it was not winning.
 */
export function leadingLabel(buckets) {
  if (!isBuckets(buckets)) return null;
  const valid = buckets.filter(
    (b) => typeof b?.label === "string" && typeof b?.p === "number" && Number.isFinite(b.p),
  );
  // The same leader the outcome list highlights, so the line and the badge agree.
  return leadingRow(valid)?.label ?? null;
}
